import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { getColors } from "../../themes/theme";
import { EstimationItem } from "./EstimationItem";
import { EstimationDialog } from "./EstimationDialog";
import { useSnackbar } from "notistack";
import { exportEstimationToPdf } from "../../utils/generateEstimationPDF";

export const GoodFaithEstimation = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token, user } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [allEstimations, setAllEstimations] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [checkedItems, setCheckedItems] = useState(new Set()); // Track checked item IDs

  const handleAddClicked = () => {
    setDialogData({ name: "", packages: [], payments: [] });
    setDialogOpen(true);
  };

  const handleGenerateClicked = async () => {
    const estimationData = []; // Initialize an empty array to store the results

    for (const id of checkedItems) {
      try {
        // Fetch the template data by ID
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/template/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Fetch the template packages by template ID
        const response1 = await axios.get(
          `${process.env.REACT_APP_API_URL}/templatepackage/templateid/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const response2 = await axios.get(
          `${process.env.REACT_APP_API_URL}/templatepayment/templateid/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Format the data as required
        const formattedData = {
          name: response.data.name,
          packages: response1.data.map((pkg) => ({
            name: pkg.name,
            visit: pkg.visit,
            cost: pkg.cost,
            totalCost: pkg.visit * pkg.cost, // Calculate Total Cost
          })),
          payments: response2.data.map((payment) => ({
            payments: payment.payments,
            discount: payment.discount,
            upfront: payment.upfront,
          })),
        };

        // Calculate Total Package Cost
        formattedData.totalPackageCost = formattedData.packages.reduce(
          (sum, pkg) => sum + pkg.totalCost,
          0
        );

        // Add the formatted data to the estimationData array
        estimationData.push(formattedData);
      } catch (error) {
        console.error(`Error fetching data for ID ${id}:`, error);
      }
    }
    exportEstimationToPdf(estimationData);
  };

  const handleSaveEstimation = async (data) => {
    try {
      if (data?.id) {
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/template/${data.id}`,
          {
            name: data.name,
            practiceid: user?.practiceId,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const formattedPackages = data.packages.map((packageItem) => ({
          name: packageItem.name,
          visit: +packageItem.visit,
          cost: +packageItem.cost,
        }));

        const formattedPayments = data.payments?.map((packageItem) => ({
          payments: packageItem.payments,
          discount: +packageItem.discount,
          upfront: +packageItem.upfront,
        }));

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepackage/bulkedit`,
          {
            templateid: response.data.id,
            packages: formattedPackages,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepayment/bulkedit`,
          {
            templateid: response.data.id,
            payments: formattedPayments,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        enqueueSnackbar("Successfully Changed", { variant: "success" });
        setAllEstimations((prev) =>
          prev
            .map((estimation) =>
              estimation.id === data.id ? response.data : estimation
            )
            .sort((a, b) => a.id > b.id)
        );
      } else {
        // Transform the packages array into the desired format

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/template`,
          {
            name: data.name,
            practiceid: user?.practiceId,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const formattedPackages = data.packages.map((packageItem) => ({
          name: packageItem.name,
          visit: +packageItem.visit,
          cost: +packageItem.cost,
        }));

        const formattedPayments = data.payments?.map((packageItem) => ({
          payments: packageItem.payments,
          discount: +packageItem.discount,
          upfront: +packageItem.upfront,
        }));

        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepackage/bulkcreate`,
          {
            templateid: response.data.id,
            packages: formattedPackages,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        await axios.post(
          `${process.env.REACT_APP_API_URL}/templatepayment/bulkcreate`,
          {
            templateid: response.data.id,
            payments: formattedPayments,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        enqueueSnackbar("Successfully Added", { variant: "success" });
        setAllEstimations((prev) => {
          const updatedEstimations = [...prev, response.data];
          return updatedEstimations.sort((a, b) => a.id > b.id);
        });
      }
    } catch (err) {
      console.error("Error saving estimation:", err);
    }
  };

  useEffect(() => {
    if (!user || !token) return;
    const fetchAllEstimations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/template/practiceid/${user?.practiceId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllEstimations(response.data);
      } catch (err) {
        console.error("Error fetching all estimations:", err);
      }
    };
    fetchAllEstimations();
  }, [user, token]);

  useEffect(() => {
    //
  }, []);

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={{
          xs: "column",
          xl: "row",
        }}
        sx={{ alignItems: "center" }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "22px", sm: "28px", md: "24px", lg: "28px" },
            fontWeight: 500,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
            mr: "auto",
            mb: { xs: 2, sm: 0, md: 2, lg: 2, xl: 0 },
          }}
        >
          Good Faith Estimation
        </Typography>

        <Box
          display="flex"
          sx={{ ml: "auto" }}
          flexDirection={{ xs: "column", sm: "row", md: "column", lg: "row" }}
          gap={3}
        >
          <Button
            variant="contained"
            sx={{
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "18px" },
              px: { xs: 2, sm: "20px" },
              py: "12px",
              background: darkMode ? "#2D2E30" : "#E6E6E6",
              color: darkMode
                ? getColors.primaryTextDark
                : getColors.secondaryTextLight,
              "&:hover": {
                color: getColors.primaryTextDark,
              },
            }}
            disabled={checkedItems.size === 0} // Disable if no items are checked
            onClick={handleGenerateClicked}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              fontFamily: "Poppins",
              fontSize: { xs: "14px", sm: "18px" },
              px: { xs: 2, sm: "20px" },
              py: "12px",
              background: darkMode ? "#2D2E30" : "#E6E6E6",
              color: darkMode
                ? getColors.primaryTextDark
                : getColors.secondaryTextLight,
              "&:hover": {
                color: getColors.primaryTextDark,
              },
            }}
            onClick={handleAddClicked}
          >
            New Template
          </Button>
        </Box>
      </Box>

      <Box
        display="grid"
        gap={4}
        sx={{
          mt: 5,
          gridTemplateColumns: {
            xs: "repeat(1, minmax(0, 1fr))",
            sm: "repeat(2, minmax(0, 1fr))",
            md: "repeat(1, minmax(0, 1fr))",
            lg: "repeat(2, minmax(0, 1fr))",
            xl: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {allEstimations.map((estimation, index) => (
          <EstimationItem
            key={index}
            {...estimation}
            onSave={handleSaveEstimation}
            onDelete={(id) =>
              setAllEstimations((prev) =>
                prev.filter((estimation) => estimation.id !== id)
              )
            }
            onCheck={(id, isChecked) => {
              setCheckedItems((prev) => {
                const updated = new Set(prev);
                if (isChecked) {
                  updated.add(id); // Add the ID if checked
                } else {
                  updated.delete(id); // Remove the ID if unchecked
                }
                return updated;
              });
            }}
          />
        ))}
      </Box>

      <EstimationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveEstimation}
        initialData={dialogData}
      />
    </Box>
  );
};
